import { APIClient } from "../helpers/api_helper";
import { ApiResponse } from "../types/api";

const api = new APIClient();

export interface ProfessionalServiceCommissionItem {
  service_id: number;
  service_name: string;
  unit_id: number;
  commission_rate_bps: number;
  is_override: boolean;
  /** ISO-8601 when this override was last saved (null if using default). */
  updated_at?: string | null;
}

export interface ProfessionalServiceCommissionsData {
  professional_id: number;
  default_commission_rate_bps: number;
  use_commission_tracking: boolean;
  restrict_mode: boolean;
  items: ProfessionalServiceCommissionItem[];
  copied_count?: number;
  skipped_count?: number;
  updated_count?: number;
}

export interface SyncProfessionalServiceCommissionItem {
  service_id: number;
  commission_rate_bps: number;
}

export interface ProfessionalServiceCommissionAuditItem {
  id: number;
  service_id: number;
  service_name: string;
  action: "created" | "updated" | "deleted" | string;
  from_rate_bps: number | null;
  to_rate_bps: number | null;
  user_name: string | null;
  created_at: string;
}

export interface ProfessionalServiceCommissionAuditsData {
  professional_id: number;
  items: ProfessionalServiceCommissionAuditItem[];
}

export const getProfessionalServiceCommissions = (
  professionalId: number
): Promise<ApiResponse<ProfessionalServiceCommissionsData>> =>
  api.get<ApiResponse<ProfessionalServiceCommissionsData>>(
    `/professionals/${professionalId}/service-commissions`
  );

export const syncProfessionalServiceCommissions = (
  professionalId: number,
  items: SyncProfessionalServiceCommissionItem[]
): Promise<ApiResponse<ProfessionalServiceCommissionsData>> =>
  api.put<ApiResponse<ProfessionalServiceCommissionsData>>(
    `/professionals/${professionalId}/service-commissions`,
    { items }
  );

export const bulkApplyProfessionalServiceCommissions = (
  professionalId: number,
  serviceIds: number[],
  commissionRateBps: number
): Promise<ApiResponse<ProfessionalServiceCommissionsData>> =>
  api.put<ApiResponse<ProfessionalServiceCommissionsData>>(
    `/professionals/${professionalId}/service-commissions/bulk`,
    { service_ids: serviceIds, commission_rate_bps: commissionRateBps }
  );

export const getProfessionalServiceCommissionAudits = (
  professionalId: number
): Promise<ApiResponse<ProfessionalServiceCommissionAuditsData>> =>
  api.get<ApiResponse<ProfessionalServiceCommissionAuditsData>>(
    `/professionals/${professionalId}/service-commissions/audits`
  );

export const undoLastProfessionalServiceCommission = (
  professionalId: number
): Promise<ApiResponse<ProfessionalServiceCommissionsData & { undone_audit_id?: number }>> =>
  api.create<ApiResponse<ProfessionalServiceCommissionsData & { undone_audit_id?: number }>>(
    `/professionals/${professionalId}/service-commissions/undo-last`,
    {}
  );

export type CopyServiceCommissionMode = "replace" | "merge";

export const copyProfessionalServiceCommissions = (
  professionalId: number,
  sourceProfessionalId: number,
  mode: CopyServiceCommissionMode = "replace"
): Promise<ApiResponse<ProfessionalServiceCommissionsData>> =>
  api.create<ApiResponse<ProfessionalServiceCommissionsData>>(
    `/professionals/${professionalId}/service-commissions/copy`,
    { source_professional_id: sourceProfessionalId, mode }
  );
